/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {ComponentPortal, ComponentType, Portal} from '@angular/cdk/portal';
import {
    AfterContentInit,
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    Optional,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {createMissingDateImplError} from './datepicker-errors';
import {matRangeDatepickerIntl} from './datepicker-intl';
import {SatMonthView} from './month-view';
import {SatMultiYearView, yearsPerPage} from './multi-year-view';
import {SatYearView} from './year-view';

import {matRangeDatepickerRangeValue} from './datepicker-input';
import {DateAdapter} from '../datetime/date-adapter';
import {MAT_DATE_FORMATS, MatDateFormats} from '../datetime/date-formats';

/**
 * Possible views for the calendar.
 * @docs-private
 */
export type SatCalendarView = 'month' | 'year' | 'multi-year';



/**
 * A calendar that is used as part of the datepicker.
 * @docs-private
 */
@Component({
  moduleId: module.id,
  selector: 'range-calendar',
  template: `<div class="from-date-sec">
  <sat-calendar cdkTrapFocus
    [id]="id"
    [ngClass]="ngClass"
    [startAt]="startAt"
    [startView]="startView"
    [minDate]="minDate"
    [maxDate]="maxDate"
    [dateFilter]="dateFilter"
    [beginDate]="beginDate"
    [endDate]="endDate"
    [rangeMode]="rangeMode"           
    [selected]="selected"
    (selectedChange)="selectedChange.emit($event)"
    (dateSelected)="_dateSelected($event)"
    (yearSelected)="yearSelected.emit($event)"
    (monthSelected)="monthSelected.emit($event)"
    >
  </sat-calendar>
</div>
<div class="end-date-sec">
  <sat-calendar cdkTrapFocus
    [id]="id"
    [ngClass]="ngClass"
    [startAt]="startAt"
    [startView]="startView"
    [minDate]="minDate"
    [maxDate]="maxDate"
    [dateFilter]="dateFilter"
    [beginDate]="beginDate"
    [endDate]="endDate"
    [rangeMode]="rangeMode"
    [selected]="selected"
    (selectedChange)="selectedChange.emit($event)"
    (dateSelected)="_dateSelected($event)"
    (yearSelected)="yearSelected.emit($event)"
    (monthSelected)="monthSelected.emit($event)"
    [nextMonth]="true"
    >
  </sat-calendar>
</div>
<div class="quick-select-sec">
  <span class="select-title">Quick Select</span>
  <ul>
    <li><button mat-button (click)="selectQuater(1)" [class.active]="checkQuarterSelected(1)">Quarter 1</button></li>
    <li><button mat-button (click)="selectQuater(2)" [class.active]="checkQuarterSelected(2)">Quarter 2</button></li>
    <li><button mat-button (click)="selectQuater(3)" [class.active]="checkQuarterSelected(3)">Quarter 3</button></li>
    <li><button mat-button (click)="selectQuater(4)" [class.active]="checkQuarterSelected(4)">Quarter 4</button></li>
  </ul>
</div>
<div class="calendar-button-sec">
  <button mat-button color="primary" (click)="onCancel()">Cancel</button>
  <button mat-raised-button color="primary" (click)="onApply()">Apply</button>
</div>`,
  styles: [`.mat-calendar{display:block}.mat-calendar-header{padding:8px 8px 0}.mat-calendar-content{padding:0 8px 8px;outline:0}.mat-calendar-controls{display:flex;margin:5% calc(33% / 7 - 16px)}.mat-calendar-spacer{flex:1 1 auto}.mat-calendar-period-button{min-width:0}.mat-calendar-arrow{display:inline-block;width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top-width:5px;border-top-style:solid;margin:0 0 0 5px;vertical-align:middle}.mat-calendar-arrow.mat-calendar-invert{-webkit-transform:rotate(180deg);transform:rotate(180deg)}[dir=rtl] .mat-calendar-arrow{margin:0 5px 0 0}.mat-calendar-next-button,.mat-calendar-previous-button{position:relative}.mat-calendar-next-button::after,.mat-calendar-previous-button::after{top:0;left:0;right:0;bottom:0;position:absolute;content:'';margin:15.5px;border:0 solid currentColor;border-top-width:2px}[dir=rtl] .mat-calendar-next-button,[dir=rtl] .mat-calendar-previous-button{-webkit-transform:rotate(180deg);transform:rotate(180deg)}.mat-calendar-previous-button::after{border-left-width:2px;-webkit-transform:translateX(2px) rotate(-45deg);transform:translateX(2px) rotate(-45deg)}.mat-calendar-next-button::after{border-right-width:2px;-webkit-transform:translateX(-2px) rotate(45deg);transform:translateX(-2px) rotate(45deg)}.mat-calendar-table{border-spacing:0;border-collapse:collapse;width:100%}.mat-calendar-table-header th{text-align:center;padding:0 0 8px}.mat-calendar-table-header-divider{position:relative;height:1px}.mat-calendar-table-header-divider::after{content:'';position:absolute;top:0;left:-8px;right:-8px;height:1px}`],
  host: {
    'class': 'range-calendar',
  },
  exportAs: 'matRangeCalendar',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeCalendar<D> {
  @Input() id: any;
  @Input() ngClass: any;
  @Input() startAt: any;
  @Input() startView: any;
  @Input() minDate: any;
  @Input() maxDate: any;
  @Input() dateFilter: any;
  @Input() beginDate: any;
  @Input() endDate: any;
  @Input() rangeMode: any;
  @Input() selected: any;
  @Output() selectedChange: any = new EventEmitter();
  @Output() dateRangesChange: any = new EventEmitter();
  @Output() yearSelected: any = new EventEmitter();
  @Output() monthSelected: any = new EventEmitter();
  @Output() _userSelection: any = new EventEmitter();
  /** Whenever user already selected start of dates interval. */
  private _beginDateSelected = false;

  constructor(_intl: matRangeDatepickerIntl,
    @Optional() private _dateAdapter: DateAdapter<D>,
    @Optional() @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats) {

    if (!this._dateAdapter) {
    throw createMissingDateImplError('DateAdapter');
    }

    if (!this._dateFormats) {
    throw createMissingDateImplError('MAT_DATE_FORMATS');
    }
  }

  _dateSelected(date: D): void {
    if (this.rangeMode) {
      if (!this._beginDateSelected) {
        this._beginDateSelected = true;
        this.beginDate = date;
        this.endDate = date;
      } else {
        this._beginDateSelected = false;
        if (this._dateAdapter.compareDate(<D>this.beginDate, date) <= 0) {
          this.dateRangesChange.emit({begin: <D>this.beginDate, end: date});
        } else {
          this.dateRangesChange.emit({begin: date, end: <D>this.beginDate});
        }
      }
    } else if (!this._dateAdapter.sameDate(date, this.selected)) {
      this.selectedChange.emit(date);
    }
  }

  _getQuarterDates(quarter: number) {
    const _year = this._dateAdapter.getYear(this._dateAdapter.today());
    const begin = this._dateAdapter.createDate(_year, (quarter - 1) * 3 , 1);
    const end = (
      quarter === 4
      ?
      this._dateAdapter.addCalendarDays(
        this._dateAdapter.createDate(_year + 1, 0, 1), 
        -1
      )
      :
      this._dateAdapter.addCalendarDays(
        this._dateAdapter.createDate(_year, quarter * 3, 1),
        -1
      )
    );
    return ({begin, end})
  }

  selectQuater(quarter: number) {
    this.dateRangesChange.emit(this._getQuarterDates(quarter));
  }

  checkQuarterSelected(quarter: number) {
    const { begin, end } = this._getQuarterDates(quarter);
    return (
      this._dateAdapter.sameDate(begin, this.beginDate)
      &&
      this._dateAdapter.sameDate(end, this.endDate)
    );
  }

  onCancel() {
    this._userSelection.emit()
  }
  onApply() {
    this._userSelection.emit({apply: true})
  }
}